import { PropageWindow } from "../abstract_classes/PropageWindow";
import { PagedJsIframeElement } from "./PagedJsIframeElement";

// Selector to get all the anchors in the PDF viewer.
// Theoretically, the script should work with other viewers
// by only changing this one line, if they render anchors correctly
const EXCLUDED_TAG_NAMES = ["table", "th", "div", "col", "code"]
const EXCLUDED_IDS = ["content"]
const PDFJS_ANCHORS_SELECTOR = 'section.linkAnnotation a[href^="#"]'

// Prefix of the automatic ID added by the Asciidoctor's extension
const AUTO_ID_PREFIX = "auto-id-"

export interface PdfJsWindow extends Window {
  PDFViewerApplication: any;
}

export class PdfJsIframeElement extends PropageWindow<PdfJsWindow> {
  pdfViewer: any;
  pagesWithEvents : number[];

  constructor(name: string, window: PdfJsWindow) {
    super(name, window, PDFJS_ANCHORS_SELECTOR, true);
    this.pagesWithEvents = [];
  }

  async init(): Promise<void> {
    this.window.onload = () => {
      this.pdfViewer = this.window.PDFViewerApplication!;
      this.add_events();
    };
    console.log(this.name, "initiated.");
  }

  get_event_elements(pageElementAnnotationlayer: HTMLElement): HTMLElement[] {
    return [...pageElementAnnotationlayer.querySelectorAll(this.selector)] as HTMLElement[];
  }

  jump(id: string): void {
    // Find the corresponding element
    let elements = this.find_id(id);
    if (elements.length > 0 && elements[0]) {
      // And smoothly scroll to it
      elements[0].style.scrollMarginTop = "50px";
      elements[0].scrollIntoView({ behavior: "smooth" });
    }
  }

  find_id(id: string): HTMLElement[] {
    let elements = [];
    // Get all the hrefs that start with  #auto-id-tag-\d+-
    let selector = '[href="#' + id + '\"]';
    EXCLUDED_IDS.forEach((excludedId) => {
      selector = selector + ':not([href^="#' + excludedId + '\"]'
    })
    elements = Array.from(this.window.document.querySelectorAll(selector)) as HTMLElement[];
    // console.log(selector, elements)
    return elements;
  }

  add_events(): void {
    // Wait for the PDFViewerApplication to initialize
    // Then various events are available: documentloaded, pagesloaded, pagerendered, annotationlayerrendered
    // https://stackoverflow.com/questions/12693207/how-to-know-if-pdf-js-has-finished-rendering/68489111#68489111
    // https://github.com/mozilla/pdf.js/wiki/Third-party-viewer-usage
    // https://github.com/mozilla/pdf.js/issues/9527
    this.pdfViewer.initializedPromise.then(() => {
      this.window.PDFViewerApplication.eventBus.on('annotationlayerrendered', (eventObject: any) => {
        // First we add the scroll events
        let elements = [...this.window.document.querySelectorAll(this.append_exceptions_to_anchor_selector(PDFJS_ANCHORS_SELECTOR))] as HTMLElement[] ;
        let scrolled_element = this.window.document.getElementById("viewerContainer")!;
        this.add_scroll_events(elements, scrolled_element);
        
        // Depending on if it is the first iteration or not,
        // the log entry has to be different.
        let action = this.pagesWithEvents.length == 0 ? "added" : "updated"
        console.log("scroll events " + action + " after p." + eventObject.pageNumber + " of " + this.name + " was loaded.");

        // Then, the hover events
        if(!(eventObject in this.pagesWithEvents)) {
          let pageElementAnnotationLayerChildren : HTMLElement[] ;
          // by selecting the children of the annotation layer of the corresponding page
          let pageAnnotationLayerAnchorsSelector = '.page[data-page-number="' + eventObject.pageNumber + '"] > .annotationLayer ' + PDFJS_ANCHORS_SELECTOR;
          pageAnnotationLayerAnchorsSelector = this.append_exceptions_to_anchor_selector(pageAnnotationLayerAnchorsSelector);
          pageElementAnnotationLayerChildren = [...this.window.document.querySelectorAll(pageAnnotationLayerAnchorsSelector)] as HTMLElement[] ;
          // and attaching them events only once
          this.add_hover_events(pageElementAnnotationLayerChildren)

          this.pagesWithEvents.push(eventObject.pageNumber);
          console.debug("hover events added for p." + eventObject.pageNumber + " of " + this.name + ".");
        }
        if(this.window.document.querySelectorAll(".page").length == this.pagesWithEvents.length) {
          this.all_events_added();
        }
      });
    });
  }

  get_id_from_event_target(event_target: HTMLElement): string {
    if (this.isLinkElement(event_target)) {
      return event_target.href.split("#").pop()!; // Remove the #
      // let idArray = id.split("-");
      // idArray.pop(); // remove the last number
      // return idArray.join("-");
    }
    throw new Error();
  }

  /**
  * Processes the id produced by Weasyprint to output the equivalent
  * id in HTML and PagedJS
  * TODO: For now, only works if AUTO_ID_PREFIX contains two dashes
  * @param {*} id An ID
  * @returns A shortened ID
  */
  get_id_from_pdfjs_id(id: string): string {
    // console.log(id)
    if(id.startsWith(AUTO_ID_PREFIX)) {
      let pos = id.lastIndexOf("-");
      return id.substring(0, pos);
    }
    throw new Error("The ids haven't been properly encoded");
  }

  highlight(id: string, state: boolean): void {
    let elements = this.find_id(id);
    if (elements.length > 0 && elements[0]) {
      elements.forEach(elem => {
        if (elem) { // Condition necessary when everything has not loaded yet
          if (state) {
            this.set_hover_style(elem, true);
          } else {
            this.unset_hover_style(elem);
          }
        }
      });
    }
  }

  isLinkElement(element: HTMLElement): element is HTMLLinkElement {
    return true;
  }

  append_exceptions_to_anchor_selector(selector: string): string {
    let new_selector = selector
    EXCLUDED_TAG_NAMES.forEach((tag) => {
      new_selector = new_selector + ":not(a[href*=\"" + tag +"\"])"
    });
    return new_selector;
  }
}


function get_original_tag_name_from_id(id: string): string {
  const regex : RegExp = /auto-id-(\w+)-/;
  if(regex.test(id)) {
    let found : string[] = id.match(regex)!;
    // console.log(found[1])
    return found[1]
  }
  throw new Error("The ids haven't been properly encoded");
}

